/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.syncAfdekkap(); // Auto-match afdekkap color to parent option
        this.updateAvailability();
        this.autoSelectFirstAvailable(); // Also run on initial page load
        this.hideFieldsetsWithOneOption(); // Hide fieldsets with ≤1 options after availability is updated
        this.hide300cmOptions(); // Hide 300cm options for non-NL/BE countries

        // Re-read variant state after sync/auto-select may have changed selections
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

        console.log('[variant-picker init]', {
          selectedOptions: this.selectedOptions,
          variant: this.variant,
          preSelection: this.preSelection,
          formInputValue: this.section.querySelector('input[name="id"]')?.value
        });

        if (this.variant) {
          this.updateVariantInput();
        }

        console.log('[variant-picker init after updateVariantInput]', {
          formInputValue: this.section.querySelector('input[name="id"]')?.value
        });

        this.updateAddToCartButton();
        this.updateSpecifications();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.syncAfdekkap(); // Auto-match afdekkap color to parent option

      // Re-read after sync may have changed selections
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAvailability();
      this.autoSelectFirstAvailable(); // Auto-select first available after marking availability
      this.hideFieldsetsWithOneOption(); // Update visibility based on available options

      // Re-read selections after auto-selection might have changed them
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      console.log('[variant-picker change]', {
        selectedOptions: this.selectedOptions,
        variant: this.variant ? { id: this.variant.id, title: this.variant.title, available: this.variant.available } : null,
        preSelection: this.preSelection,
        formInputValue: this.section.querySelector('input[name="id"]')?.value
      });

      // Update variant-specific data if we now have a valid variant after auto-selection
      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      // Update button and UI with the correct variant data
      this.updateAddToCartButton();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      this.updateSpecifications();
      this.updateAllLabelTexts(); // Update all color labels based on current selections

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Auto-selects the first available option in fieldsets where
     * the currently selected option is unavailable.
     * Runs after updateAvailability() marks options.
     * Does NOT dispatch events - just silently updates the radio button state.
     */
    autoSelectFirstAvailable() {
      this.optionSelectors.forEach((selector, selectorIndex) => {
        // Skip dropdown selectors - they handle their own selection
        if (selector.dataset.selectorType === 'dropdown') {
          return;
        }

        // Get currently checked input in this fieldset
        const currentlyChecked = selector.querySelector('input:checked');

        // If nothing is checked, or if checked option is unavailable, auto-select first available
        if (!currentlyChecked || currentlyChecked.classList.contains('is-unavailable')) {
          // Find first available option (not marked unavailable and not empty placeholder)
          const firstAvailable = selector.querySelector(
            'input.js-option:not(.is-unavailable):not([value=""])'
          );

          if (firstAvailable && firstAvailable !== currentlyChecked) {
            // Uncheck current option if it exists
            if (currentlyChecked) {
              currentlyChecked.checked = false;
            }

            // Check the first available option
            firstAvailable.checked = true;

            // NOTE: Do NOT dispatch change event - let the calling handler
            // re-read selections and update the UI
          }
        }
      });
    }

    /**
     * Hides fieldsets marked as mounting/installation set (data-is-mounting-set from Liquid)
     * when they have ≤1 available option, or hides individual unavailable options when 2+.
     */
    hideFieldsetsWithOneOption() {
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          return;
        }
        if (selector.dataset.isMountingSet !== 'true') {
          return;
        }

        const allOptions = selector.querySelectorAll('input.js-option:not([value=""])');
        const availableOptions = selector.querySelectorAll(
          'input.js-option:not(.is-unavailable):not([value=""])'
        );

        if (availableOptions.length <= 1) {
          selector.style.display = 'none';
          allOptions.forEach((input) => {
            const label = input.nextElementSibling;
            if (label) label.style.display = '';
          });
        } else {
          selector.style.display = '';
          allOptions.forEach((input) => {
            const label = input.nextElementSibling;
            if (label) {
              label.style.display = input.classList.contains('is-unavailable') ? 'none' : '';
            }
          });
        }
      });
    }

    /**
     * Auto-syncs afdekkap (cover cap) option to match the parent option's color,
     * then hides the afdekkap fieldset. Uses data-afdekkap-parent index set by Liquid.
     * Matches by case-insensitive prefix comparison (e.g. "ALU" → "Aluminium").
     */
    syncAfdekkap() {
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.isAfdekkap !== 'true') return;

        const parentIndex = parseInt(selector.dataset.afdekkapParent, 10);
        if (isNaN(parentIndex)) return;

        const parentSelector = this.optionSelectors[parentIndex];
        if (!parentSelector) return;

        // Get selected value from parent option
        let parentValue = null;
        if (parentSelector.dataset.selectorType === 'dropdown') {
          const customSelect = parentSelector.querySelector('custom-select');
          if (customSelect) parentValue = customSelect.value;
        } else {
          const checked = parentSelector.querySelector('input:checked');
          if (checked) parentValue = checked.value;
        }

        if (!parentValue) return;

        // Find best matching option in afdekkap (case-insensitive prefix match)
        // Then verify the resulting variant is available; fall back to first available option.
        const parentLower = parentValue.toLowerCase();
        const selectorIndex = Array.from(this.optionSelectors).indexOf(selector);
        const options = selector.querySelectorAll('input.js-option');
        let bestMatch = null;

        options.forEach((opt) => {
          const val = opt.value.toLowerCase();
          if (val === parentLower || val.startsWith(parentLower) || parentLower.startsWith(val)) {
            bestMatch = opt;
          }
        });

        // Check if the best color match leads to an available variant
        if (bestMatch && selectorIndex >= 0) {
          const testOptions = [...this.getSelectedOptions()];
          testOptions[selectorIndex] = bestMatch.value;
          const matchedVariant = this.data.product.variants.find(
            (v) => v.options.every((val, i) => val === testOptions[i])
          );
          if (!matchedVariant || !matchedVariant.available) {
            // Color match is unavailable — find first option that gives an available variant
            let fallback = null;
            options.forEach((opt) => {
              if (!fallback) {
                const tryOptions = [...testOptions];
                tryOptions[selectorIndex] = opt.value;
                const tryVariant = this.data.product.variants.find(
                  (v) => v.options.every((val, i) => val === tryOptions[i])
                );
                if (tryVariant && tryVariant.available) {
                  fallback = opt;
                }
              }
            });
            if (fallback) bestMatch = fallback;
          }
        }

        if (bestMatch && !bestMatch.checked) {
          options.forEach((o) => { o.checked = false; });
          bestMatch.checked = true;
        }

        // Always hide the afdekkap fieldset
        selector.style.display = 'none';
      });
    }

    /**
     * Hides 300cm options across all option positions unless on artiteq.com NL/BE.
     */
    hide300cmOptions() {
      if (this.getAttribute('data-allow-300cm') === 'true') {
        return;
      }

      this.optionSelectors.forEach((selector) => {
        const isDropdown = selector.dataset.selectorType === 'dropdown';
        const options = selector.querySelectorAll('.js-option');

        options.forEach((option) => {
          const rawValue = (isDropdown ? option.dataset.value : option.value) || '';
          const value = rawValue.toLowerCase();
          const has300cm =
            value.includes('300 cm') ||
            value.includes('300cm') ||
            value.includes('300 cent') ||
            value.includes('300 zent');

          if (!has300cm) return;

          if (isDropdown) {
            option.style.display = 'none';
          } else {
            const label = option.nextElementSibling;
            if (label) label.style.display = 'none';
          }

          // Mark as unavailable so it doesn't get selected
          option.classList.add('is-unavailable');
          option.classList.add('is-nonexistent');
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the label text for all option selectors with color labels.
     * Checks the currently selected input in each fieldset and updates the label accordingly.
     */
    updateAllLabelTexts() {
      this.optionSelectors.forEach((selector) => {
        // Skip dropdown selectors
        if (selector.dataset.selectorType === 'dropdown') return;

        // Find the color text label in this selector
        const colorText = selector.querySelector('.js-color-text');
        if (!colorText) return;

        // Find the currently checked input
        const checkedInput = selector.querySelector('input:checked');
        if (!checkedInput) return;

        // Get the value text from the label
        const valueElement = checkedInput.nextElementSibling?.querySelector('.js-value');
        if (valueElement) {
          colorText.textContent = valueElement.textContent;
        }
      });
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available && !this.preSelection);
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;
      const from = this.price.querySelector('.price__from');
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Gets the translated label for a specification key.
     * @param {string} key - The specification key.
     * @param {object} spec - The specification object.
     * @returns {string} The translated label.
     */
    getSpecLabel(key, spec) {
      // Priority order for finding the label:
      // 1. spec.label (pre-translated in JSON)
      if (spec.label) {
        // console.log(`[Spec Label] Using spec.label for "${key}":`, spec.label);
        return spec.label;
      }

      // 2. theme.strings.specs[key] (common pattern)
      if (window.theme && theme.strings && theme.strings.specs && theme.strings.specs[key]) {
        // console.log(`[Spec Label] Using theme.strings.specs for "${key}":`, theme.strings.specs[key]);
        return theme.strings.specs[key];
      }

      // 3. theme.strings.specLabels[key] (alternative pattern)
      if (window.theme && theme.strings && theme.strings.specLabels && theme.strings.specLabels[key]) {
        // console.log(`[Spec Label] Using theme.strings.specLabels for "${key}":`, theme.strings.specLabels[key]);
        return theme.strings.specLabels[key];
      }

      // 4. theme.strings.specifications[key] (another alternative)
      if (window.theme && theme.strings && theme.strings.specifications && theme.strings.specifications[key]) {
        // console.log(`[Spec Label] Using theme.strings.specifications for "${key}":`, theme.strings.specifications[key]);
        return theme.strings.specifications[key];
      }

      // 5. Look for translation in data.translations
      if (this.data.translations && this.data.translations.specs && this.data.translations.specs[key]) {
        // console.log(`[Spec Label] Using data.translations.specs for "${key}":`, this.data.translations.specs[key]);
        return this.data.translations.specs[key];
      }

      // 6. Format the key nicely as fallback (e.g., "screen_size" -> "Screen Size")
      // console.warn(`[Spec Label] No translation found for "${key}", using formatted key as fallback`);
      // console.log('[Spec Label] Available translation sources:', {
      //   'spec object': spec,
      //   'theme.strings': window.theme && theme.strings,
      //   'data.translations': this.data.translations
      // });
      return this.formatSpecKey(key);
    }

    /**
     * Formats a spec key into a readable label.
     * @param {string} key - The specification key.
     * @returns {string} Formatted label.
     */
    formatSpecKey(key) {
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    /**
     * Updates the product specifications based on selected variant.
     */
    updateSpecifications() {
      this.specsTable = this.specsTable || document.querySelector('.js-variant-specs');
      if (!this.specsTable) return;

      const specsBody = this.specsTable.querySelector('.js-specs-body');
      if (!specsBody) return;

      // Clear existing rows
      specsBody.innerHTML = '';

      // Get specifications for current variant
      const variantSpecs = this.data.specifications && this.data.specifications[this.variant?.id];
      if (!variantSpecs) return;

      // Create new rows for this variant's specifications
      Object.entries(variantSpecs).forEach(([key, spec]) => {
        const row = document.createElement('tr');
        row.className = 'spec-row';
        row.setAttribute('data-spec-key', key);

        const labelCell = document.createElement('td');
        labelCell.className = 'spec-label';
        labelCell.textContent = this.getSpecLabel(key, spec);
        
        const valueCell = document.createElement('td');
        valueCell.className = 'spec-value';
        valueCell.textContent = spec.value || spec;
        
        row.appendChild(labelCell);
        row.appendChild(valueCell);
        specsBody.appendChild(row);
      });
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
          selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      // console.log('data: ', JSON.parse(dataEl.textContent));
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}